import { useState } from "react";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Crosshair, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";

/**
 * Start + End address picker with GPS auto-fill for the pickup address
 * and manual fallback input when cell service is poor. Used on Group Ride,
 * City, Custom, Meter flows.
 *
 * v2.6.9 batch BT · `gpsOnly` prop hides the Manual address-entry toggle
 * for the driver Live Meter flow ("tap and go" — no friction). Other flows
 * (rider booking, group rides) keep the manual fallback for offline use.
 */
export default function StartEndPicker({ origin, setOrigin, destination, setDestination, testidPrefix = "trip", gpsOnly = false }) {
  const [locating, setLocating] = useState(false);
  const [manual, setManual] = useState(false);

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("GPS not available");
    setLocating(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude: lat, longitude: lng } = pos.coords;
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      let addr = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
      if (apiKey) {
        try {
          const r = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`);
          const j = await r.json();
          if (j.results?.[0]?.formatted_address) addr = j.results[0].formatted_address;
        } catch {}
      }
      setOrigin(addr);
      toast.success("Pickup set to your current location");
      setLocating(false);
    }, (err) => {
      toast.error("GPS error: " + err.message);
      setLocating(false);
    }, { enableHighAccuracy: true, timeout: 8000 });
  };

  return (
    <div className="space-y-3">
      <div>
        <div className="flex items-center justify-between mb-1">
          <Label className="text-[10px] uppercase tracking-widest text-zinc-400">Start (Pickup)</Label>
          <div className="flex gap-1">
            <Button type="button" data-testid={`${testidPrefix}-use-gps`} onClick={useMyLocation} disabled={locating} size="sm" variant="ghost" className="h-6 text-[10px] text-amber-400 hover:bg-amber-400/10">
              {locating ? <Loader2 className="h-3 w-3 animate-spin"/> : <Crosshair className="h-3 w-3 mr-1"/>}
              {locating ? "..." : "USE GPS"}
            </Button>
            {!gpsOnly && (
              <Button type="button" data-testid={`${testidPrefix}-manual-toggle`} onClick={() => setManual(m => !m)} size="sm" variant="ghost" className="h-6 text-[10px] text-zinc-400 hover:bg-zinc-800">
                {manual ? "AUTO" : "MANUAL"}
              </Button>
            )}
          </div>
        </div>
        {manual ? (
          <Input data-testid={`${testidPrefix}-origin-manual`} value={origin} onChange={(e) => setOrigin(e.target.value)} placeholder="Type address manually (no internet)" className="bg-zinc-950 border-zinc-700 h-12"/>
        ) : (
          <AddressAutocomplete value={origin} onChange={setOrigin} placeholder="Pickup address" testid={`${testidPrefix}-origin`}/>
        )}
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-widest text-zinc-400">End (Drop-off)</Label>
        {manual ? (
          <Input data-testid={`${testidPrefix}-destination-manual`} value={destination} onChange={(e) => setDestination(e.target.value)} placeholder="Type drop-off manually" className="bg-zinc-950 border-zinc-700 h-12"/>
        ) : (
          <AddressAutocomplete value={destination} onChange={setDestination} placeholder="Drop-off address" testid={`${testidPrefix}-destination`}/>
        )}
      </div>
    </div>
  );
}
